<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Loading overlay -->
    <div
      v-if="!isBackgroundReady"
      class="fixed inset-0 bg-black z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"
        ></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div
      class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 sm:py-12 lg:px-0 lg:py-0"
    >
      <!-- TOP: Logo (mobile only) + Theme toggle -->
      <div
        class="flex items-center justify-between w-full max-w-sm mx-auto lg:max-w-none lg:justify-end lg:px-12 lg:pt-8 lg:pb-4"
      >
        <!-- Logo (visible only on mobile) -->
        <div class="lg:hidden">
          <img
            :src="logoUrl"
            :class="[
              'h-12 w-auto',
              { 'logo-invert-dark': isDnwaysOrBritech || isGenial },
            ]"
            :alt="nomeEmpresa"
            @touchstart="startLogoPress"
            @touchend="endLogoPress"
            @touchcancel="endLogoPress"
          />
        </div>

        <div class="flex items-center gap-2">
          <!-- Background Customizer (DNWAYS only) -->
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />

          <!-- Theme Toggle -->
          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Toggle theme"
            :aria-pressed="isDark"
          >
            <svg
              v-if="!isDark"
              class="w-5 h-5 text-yellow-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
              />
            </svg>
          </button>

          <!-- Secret input for editor mode activation -->
          <input
            v-if="showSecretInput"
            v-model="secretInput"
            @input="checkSecretCode"
            ref="secretInput"
            type="text"
            class="fixed -left-full opacity-0 w-0 h-0"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- CENTER: Main content -->
      <div
        class="flex-1 flex flex-col justify-center w-full"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <div
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
        <!-- Logo com backdrop (desktop only) -->
        <div class="hidden lg:flex justify-center mb-10">
          <div
            :class="[
              'inline-flex items-center justify-center px-3 py-2 rounded-xl',
              { 'bg-black/10': !isFormStylingActive }
            ]"
          >
            <img
              :src="logoUrl"
              :class="[
                'h-20 lg:h-24 w-auto',
                { 'logo-invert-dark': !isFormStylingActive && (isDnwaysOrBritech || isGenial), 'logo-invert': shouldInvertLogo },
              ]"
              :alt="nomeEmpresa"
            />
          </div>
        </div>

        <!-- Descrição -->
        <div class="flex justify-center mb-8">
          <span class="text-sm text-white/60 text-center">
            Clique abaixo para confirmar sua conta
          </span>
        </div>

        <!-- Botão de confirmar -->
        <Button
          type="button"
          @click="confirmarConta"
          :loading="disableBtnLogin"
          :disabled="disableBtnLogin"
          label="Confirmar conta"
          class="w-full btn-dynamic-color"
          :style="
            customButtonColor
              ? `background-color: ${customButtonColor} !important; border-color: ${customButtonColor} !important;`
              : ''
          "
        />

        <!-- Link voltar -->
        <div class="mt-6 text-center">
          <router-link
            to="/login"
            class="text-sm font-medium text-white/60 hover:text-white transition-colors"
          >
            Voltar para login
          </router-link>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios";
import { useEditorMode } from "@/composables/useEditorMode";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import Button from "primevue/button";

export default {
  name: "ConfirmarConta",
  components: {
    BackgroundCustomizer,
    Button,
  },
  data() {
    return {
      nomeEmpresa: "",
      guidCliente: "",
      messageError: null,
      erro: false,
      disableBtnLogin: false,
      customBackgroundStyle: {
        background: "linear-gradient(135deg, #00235C, #003D82, #0056A8)",
      },
      customButtonColor: null,
      isBackgroundReady: false,
      formStylingConfig: null,
      isDark: true,
      logoUrl: "",
      showBackgroundCustomizer: false,
      showSecretInput: false,
      secretInput: "",
      themeClickCount: 0,
      logoTouchStart: null,
    };
  },
  computed: {
    isDnwaysOrBritech() {
      return (
        this.nomeEmpresa === "DNWAYS" ||
        this.nomeEmpresa === "BRITECH" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X"
      );
    },
    isGenial() {
      return this.nomeEmpresa === "GENIAL";
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width < 768) return 'mobile';
      if (width < 1024) return 'tablet';
      return 'desktop';
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    currentFormConfig() {
      if (!this.isFormStylingActive) return null;
      return this.formStylingConfig?.[this.detectedDevice] || null;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } = this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: 'blur(16px)',
        borderRadius: '1rem',
        padding: '2rem',
        width: width || '100%',
        minWidth: minWidth || '0px',
        maxWidth: maxWidth || '600px',
        marginLeft: margin || '0rem',
        marginRight: margin || '0rem'
      };
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return '';
      const { alignment } = this.currentFormConfig;
      return alignment === 'left' ? 'mr-auto' : 
             alignment === 'right' ? 'ml-auto' : 'mx-auto';
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return 'items-center mx-auto';
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === 'left') {
        return 'items-start';
      } else if (alignment === 'right') {
        return 'items-end';
      } else {
        return 'items-center mx-auto';
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return {};
      }
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};
      
      if (alignment === 'left') {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === 'right') {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }
      
      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return 'w-full';
      }
      return '';
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return this.isDnwaysOrBritech || this.isGenial;
      }
      return this.currentFormConfig.logoInvert;
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return '';
      const config = this.currentFormConfig;
      return `
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p {
          color: ${config.textColor} !important;
          background: none !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled {
          background: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
          border-color: ${this.hexToRgba(config.buttonColorType1, config.buttonOpacityType1 / 100)} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border {
          background-color: ${this.hexToRgba(config.buttonColorType2, config.buttonOpacityType2 / 100)} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
  },
  mounted() {
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    this.guidCliente = this.$route.params.id;
    this.disableBtnLogin = false;
    this.loadLogoUrl();

    const { editorModeActive } = useEditorMode();
    this.showBackgroundCustomizer = editorModeActive.value;
  },
  methods: {
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleFormStyling(config) {
      this.formStylingConfig = config;
    },
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
      console.log('✅ Background aplicado e pronto para exibição');
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    async confirmarConta() {
      this.disableBtnLogin = true;
      if (this.guidCliente == "") {
        this.messageError = "Verifique os dados";
        this.erro = true;
        this.disableBtnLogin = false;
        return;
      }

      var model = { ClienteGuid: this.guidCliente };

      await api
        .post("/register/ativar-conta", model)
        .then((retorno) => {
          this.disableBtnLogin = false;
          if (!retorno.data) {
            this.$swal({
              icon: "error",
              title: "Oops...",
              text: `Falha na validação da conta`,
            });
          } else {
            this.$swal({
              icon: "success",
              title: "E-mail validado com sucesso!",
              text: `Faça o login para utilizar sua conta.`,
            });

            this.$router.replace({
              path: "/login",
            });
          }
        })
        .catch((error) => {
          this.disableBtnLogin = false;
          this.$swal({
            icon: "error",
            title: "Oops...",
            text: `${error.response.data.descricao}`,
          });
        });
    },
    loadLogoUrl() {
      const logoMap = {
        BRITECH: require("@/assets/logoHeader.png"),
        DNWAYS_DEMO_Y: require("@/assets/logoHeader.png"),
        DNWAYS_DEMO_X: require("@/assets/logoHeader.png"),
        DNWAYS: require("@/assets/logoHeader.png"),
        Vortex: require("@/assets/dnways.png"),
        ACTUAL: require("@/assets/logoActual_v2.png"),
        GENIAL: require("@/assets/logogenial.png"),
        RJI: require("@/assets/logoHeader_rji.png"),
      };
      this.logoUrl =
        logoMap[this.nomeEmpresa] || require("@/assets/logoHeader.png");
    },
    handleThemeClick() {
      this.themeClickCount++;
      if (this.themeClickCount === 5) {
        this.showSecretInput = true;
        this.$nextTick(() => {
          this.$refs.secretInput?.focus();
        });
        this.themeClickCount = 0;
      }
      this.isDark = !this.isDark;
      
      // Sincroniza o modo interno do form styling com o tema visual
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? 'dark' : 'light';
        console.log(`🔄 Sincronizando form styling para modo ${newMode} (tema visual alterado)`);
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }
    },
    checkSecretCode() {
      if (this.secretInput.toLowerCase() === "editor") {
        this.showBackgroundCustomizer = true;
        this.showSecretInput = false;
        this.secretInput = "";
      }
    },
    startLogoPress() {
      this.logoTouchStart = Date.now();
    },
    endLogoPress() {
      const touchDuration = Date.now() - (this.logoTouchStart || 0);
      if (touchDuration >= 3000) {
        this.showBackgroundCustomizer = true;
      }
      this.logoTouchStart = null;
    },
  },
};
</script>

<style scoped v-html="dynamicTextStyles"></style>
